// components/reports/CustomerReport.jsx
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Users,
  UserPlus,
  TrendingUp,
  Star,
  Phone,
  Calendar,
  CreditCard,
  MapPin,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { EnhancedBarChart } from '../../components/charts/EnhancedBarChart';

export function CustomerReport({ data, orderData, onExport, onViewDetails }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  if (!data || !orderData) return null;

  // Process customer data from orderData
  const processCustomerData = () => {
    const customerMap = {};
    
    orderData.forEach(order => {
      // Group by phone number or customer name
      const key = order.CustomerId || order.PhoneNumber || order.CustomerName || 'anonymous';
      
      if (!customerMap[key]) {
        customerMap[key] = {
          name: order.CustomerName || 'Anonymous',
          phone: order.PhoneNumber || 'N/A',
          orderCount: 0,
          totalSpent: 0,
          lastOrder: order.OrderDateTime,
          firstOrder: order.OrderDateTime,
          avgOrderValue: 0,
          status: order.OrderStatus,
          orderIds: []
        };
      }
      
      customerMap[key].orderCount++;
      customerMap[key].totalSpent += order.NetAmount || 0;
      customerMap[key].lastOrder = new Date(order.OrderDateTime) > new Date(customerMap[key].lastOrder) 
        ? order.OrderDateTime 
        : customerMap[key].lastOrder;
      customerMap[key].firstOrder = new Date(order.OrderDateTime) < new Date(customerMap[key].firstOrder) 
        ? order.OrderDateTime 
        : customerMap[key].firstOrder;
      customerMap[key].orderIds.push(order.OrderMasterId);
    });

    // Calculate averages and categorize
    const customers = Object.values(customerMap).map(customer => ({
      ...customer,
      avgOrderValue: customer.totalSpent / customer.orderCount,
      customerType: customer.orderCount === 1 ? 'New' : customer.orderCount > 5 ? 'Regular' : 'Returning',
      lifetimeValue: customer.totalSpent
    }));

    // Customer type distribution
    const typeData = {
      New: customers.filter(c => c.customerType === 'New').length,
      Returning: customers.filter(c => c.customerType === 'Returning').length,
      Regular: customers.filter(c => c.customerType === 'Regular').length
    };

    // Lifetime value segments
    const valueSegments = {
      'High (>RS1000)': customers.filter(c => c.lifetimeValue > 1000).length,
      'Medium (RS500-RS1000)': customers.filter(c => c.lifetimeValue >= 500 && c.lifetimeValue <= 1000).length,
      'Low (<RS500)': customers.filter(c => c.lifetimeValue < 500).length
    };

    return {
      customers,
      typeData: Object.entries(typeData).map(([type, count]) => ({ name: type, value: count })),
      valueSegments: Object.entries(valueSegments).map(([segment, count]) => ({ name: segment, value: count })),
      totalCustomers: customers.length,
      newCustomers: customers.filter(c => c.customerType === 'New').length,
      avgLifetimeValue: customers.reduce((sum, c) => sum + c.lifetimeValue, 0) / customers.length,
      repeatRate: Math.round((customers.filter(c => c.orderCount > 1).length / customers.length) * 100)
    };
  };

  const customerData = processCustomerData();

  // Filter and sort customers
  const filteredCustomers = useMemo(() => {
    let result = [...customerData.customers];

    // Apply search
    if (searchTerm) {
      result = result.filter(customer => 
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (customerTypeFilter !== 'all') {
      result = result.filter(customer => customer.customerType === customerTypeFilter);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result.slice(0, 10); // Limit to 10 for preview
  }, [customerData.customers, searchTerm, customerTypeFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getCustomerTypeBadge = (type) => {
    switch(type) {
      case 'New':
        return <Badge className="bg-blue-500 hover:bg-blue-600">New</Badge>;
      case 'Returning':
        return <Badge className="bg-green-500 hover:bg-green-600">Returning</Badge>;
      case 'Regular':
        return <Badge className="bg-purple-500 hover:bg-purple-600">Regular</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getValueSegment = (value) => {
    if (value > 1000) return 'High (>Rs1000)';
    if (value >= 500) return 'Medium (Rs500-Rs1000)';
    return 'Low (<Rs500)';
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Customers</p>
                <p className="text-2xl font-bold">{customerData.totalCustomers}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+{customerData.newCustomers} new</span>
              <span className="text-sm text-muted-foreground ml-2">this month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Lifetime Value</p>
                <p className="text-2xl font-bold">
                  Rs {customerData.avgLifetimeValue.toFixed(0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+12.5%</span>
              <span className="text-sm text-muted-foreground ml-2">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Repeat Rate</p>
                <p className="text-2xl font-bold">{customerData.repeatRate}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-purple-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+5.2%</span>
              <span className="text-sm text-muted-foreground ml-2">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">New Customers</p>
                <p className="text-2xl font-bold">{customerData.newCustomers}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <Star className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+8.7%</span>
              <span className="text-sm text-muted-foreground ml-2">vs last month</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        <EnhancedBarChart
          data={customerData.typeData}
          title="Customer Type Distribution"
          color="#3b82f6"
          onExport={() => onExport && onExport('customers')}
        />
        
        <EnhancedBarChart
          data={customerData.valueSegments}
          title="Customer Value Segments"
          color="#10b981"
          onExport={() => onExport && onExport('customers')}
        />
      </div>

      {/* Customer List Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Users className="h-5 w-5" />
              Top Customers
            </CardTitle>
            
            <div className="flex flex-col md:flex-row gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search customers..."
                  className="pl-9 md:w-[200px]"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Select value={customerTypeFilter} onValueChange={setCustomerTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Customer Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="New">New Customers</SelectItem>
                  <SelectItem value="Returning">Returning</SelectItem>
                  <SelectItem value="Regular">Regular</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                    Customer
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('phone')}>
                    Contact
                  </TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('orderCount')}>
                    Orders
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('totalSpent')}>
                    Total Spent
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('avgOrderValue')}>
                    Avg Order
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('lastOrder')}>
                    Last Order
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium">
                            {customer.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {getValueSegment(customer.lifetimeValue)}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{customer.phone}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getCustomerTypeBadge(customer.customerType)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-bold">{customer.orderCount}</span>
                        <Badge variant="outline" className="text-xs">
                          #{index + 1}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="font-bold">
                      Rs {customer.totalSpent.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      Rs {customer.avgOrderValue.toFixed(0)}
                    </TableCell>
                    <TableCell>
                      {new Date(customer.lastOrder).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => onViewDetails && onViewDetails('customer-details')}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredCustomers.length === 0 && (
              <div className="text-center py-12">
                <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold">No customers found</h3>
                <p className="text-muted-foreground">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {filteredCustomers.length} of {customerData.totalCustomers} customers
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onExport && onExport('customers')}>
                <Download className="h-4 w-4 mr-2" />
                Export Customers
              </Button>
              <Button onClick={() => onViewDetails && onViewDetails('customers')}>
                <Eye className="h-4 w-4 mr-2" />
                View All Customers
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Customer Insights */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Most Valuable</p>
                <p className="text-xl font-bold">
                  {customerData.customers.sort((a, b) => b.totalSpent - a.totalSpent)[0]?.name || 'N/A'}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <Star className="h-5 w-5 text-yellow-500" />
              </div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              Rs {customerData.customers.sort((a, b) => b.totalSpent - a.totalSpent)[0]?.totalSpent.toLocaleString() || '0'} lifetime value
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Most Frequent</p>
                <p className="text-xl font-bold">
                  {customerData.customers.sort((a, b) => b.orderCount - a.orderCount)[0]?.name || 'N/A'}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-500" />
              </div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {customerData.customers.sort((a, b) => b.orderCount - a.orderCount)[0]?.orderCount || '0'} orders
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Recent Joiner</p>
                <p className="text-xl font-bold">
                  {customerData.customers.sort((a, b) => new Date(b.firstOrder) - new Date(a.firstOrder))[0]?.name || 'N/A'}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-blue-500" />
              </div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {new Date(customerData.customers.sort((a, b) => new Date(b.firstOrder) - new Date(a.firstOrder))[0]?.firstOrder || new Date()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}